import {useEffect, useRef, useState} from 'react';
import {createTypingTracker} from './typingTracker';
import {isCheckError} from './CheckErrorNotice';
import {collectEditableFiles, getCodeTaskContent, getCodeTaskFiles, getCodeTaskLanguage, isCodeTaskDetails} from './CodeTaskResults';

// Язык Monaco по имени файла; SQL-задание (codeType 'sql') — всегда sql, файлов у него нет.
export function getEditorLanguage(fileName, codeType, lang) {
    if (codeType === 'sql') return 'sql';
    if (codeType === 'code') return getCodeTaskLanguage(fileName, lang);
    let arr = (fileName || '').split('.');
    let last = arr[arr.length - 1];
    return last === 'css' ? 'css' : last === 'html' ? 'html' : last === 'ts' ? 'typescript' : last === 'sql' ? 'sql' : 'javascript';
}

export function useCodeRun(props) {
    let {isExam, onChangeCode, onChangeLogs} = props;

    let [code, setCode] = useState('');
    let [logsReader, setLogsReader] = useState('[]');
    let [forceRenderLogs, setForceRenderLogs] = useState(-1);
    let [history, setHistory] = useState(-1);
    let [files, setFiles] = useState([]);
    let [selectedFileInd, setSelectedFileInd] = useState(0);
    let [runLoading, setRunLoading] = useState(false);
    let [curCasesStr, setCurCasesStr] = useState('');
    let [cases, setCases] = useState([]);
    let [jsDetails, setJsDetails] = useState({});
    let [topTab, setTopTab] = useState('condition');
    let [runResults, setRunResults] = useState({});
    let [runSubmitResults, setRunSubmitResults] = useState({});
    let [submissionId, setSubmissionId] = useState(null);
    let [activeCaseInd, setActiveCaseInd] = useState(0);
    let [data, setData] = useState({});
    const codeRef = useRef('');
    const startTimeRef = useRef(0);
    const typingRef = useRef(null);
    if (!typingRef.current) {
        typingRef.current = createTypingTracker({post: body => global.http.post('/code-typing', body, {wo_notify: true})});
    }

    let question = props.question || (data || {}).question || {};

    global.setHistoryObj = (histObj) => {
        setData(d => ({...d, historyObj: histObj[getQuestionId()]}));
    };

    useEffect(() => {
        startTimeRef.current = new Date().getTime();
    }, []);

    useEffect(() => {
        initExam();
    }, [(props.question || {})._id]);

    function getQuestionId() {
        return new URLSearchParams(window.location.search).get('question') || window.location.href.split('=')[1] || 1061;
    }

    function getFileName(ind = selectedFileInd, _files) {
        ind = ind || 0;
        return ((_files || files || [])[ind] || {}).name || '';
    }

    function getStarter(_jsDetails, ind = 0, fname) {
        let fileName = fname || getFileName(ind);
        if (isCodeTaskDetails(_jsDetails)) return getCodeTaskContent(_jsDetails, null, fileName);
        return ((_jsDetails.starterFiles || {})[fileName] || '') || (_jsDetails.starter || '') || '';
    }

    function isLogsRaederFn(_jsDetails) {
        return (_jsDetails || {}).codeType === 'logreader';
    }

    function getFileExt() {
        return getEditorLanguage(getFileName(selectedFileInd), (jsDetails || {}).codeType, (jsDetails || {}).lang);
    }

    function isReadOnlyFile() {
        return isCodeTaskDetails(jsDetails) && !!(files[selectedFileInd] || {}).readOnly;
    }

    function updateCode(newCode) {
        codeRef.current = newCode;
        setCode(newCode);
    }

    function buildTestCases(str, fields) {
        let size = (fields || []).length;
        if (!size) return [];
        let res = [];
        let arr = (str || '').split('\n');
        try {
            for (let i = 0; i < arr.length; i += fields.length) {
                let d = [];
                for (let j = 0; j < size; j++) {
                    let it;
                    try { it = JSON.parse(arr[i + j]); } catch (e) { it = arr[i + j]; }
                    d.push(it);
                }
                res.push(d);
            }
        } catch (e) {}
        return res;
    }

    function getTestCase() {
        return (cases || [])[activeCaseInd || 0] || '';
    }

    function initExam() {
        let {jsDetails: _jsDetails = {}, history: _history = {}, runResults: _runResults} = props;
        _history ??= {};
        _history.files ??= {};

        let str = _history.testCasesStr || _jsDetails.curCasesStr || _jsDetails.pubCasesStr || '';
        let builtCases = buildTestCases(str, _jsDetails.fields);
        let _files = isCodeTaskDetails(_jsDetails) ? getCodeTaskFiles(_jsDetails) : _jsDetails.files;
        // у code-задачи первым открывается первый редактируемый файл
        let firstEditable = isCodeTaskDetails(_jsDetails) ? _files.findIndex(f => !f.readOnly) : 0;
        firstEditable = Math.max(firstEditable, 0);
        let fileName = getFileName(firstEditable, _files);
        let __code = isCodeTaskDetails(_jsDetails)
            ? getCodeTaskContent(_jsDetails, _history, fileName)
            : !isLogsRaederFn(_jsDetails)
            ? (_history.files || {})[fileName] || getStarter(_jsDetails, 0, fileName)
            : getStarter(_jsDetails, 0, fileName);

        updateCode(__code);
        setLogsReader(_history.logsReader || '[]');
        setCurCasesStr(str);
        setHistory(_history);
        setCases(builtCases);
        setJsDetails(_jsDetails);
        setRunResults(_runResults);
        setFiles(_files || []);
        setSelectedFileInd(firstEditable);
    }

    function run(params) {
        setTopTab('results');
        setRunLoading(params && params.isSubmit ? 'submit' : 'run');
        global.http.post('/run-question', {
            curCasesStr,
            // Проверка знаний курса (courseCheck) встроена как isExam, но не экзамен: сдача уходит в ИИ-проверку.
            isExam: isExam && !props.courseCheck,
            files: isCodeTaskDetails(jsDetails)
                ? collectEditableFiles(jsDetails, history, getFileName(), codeRef.current)
                : {'': codeRef.current},
            logsReader,
            typingHash: typingRef.current.getHash(),
            question: question._id || getQuestionId(),
            courseModule: props.courseModule,
            ...params
        })
            .then(r => {
                setRunLoading(false);
                // Сбой проверки пришёл в теле 200: не засчитываем ни успех, ни провал, статус не трогаем.
                if (isCheckError(r)) return showCheckError(params && params.isSubmit, r.checkError);
                let {wrongCount} = r;

                function trySet(key, keys) {
                    let curStatus = (data.historyObj || {}).status;
                    if (keys.indexOf(curStatus) < 0) {
                        try { Storage.changeStatus({_id: getQuestionId(), status: key}); } catch (e) {}
                    }
                }

                if (params && params.isSubmit) {
                    !wrongCount && trySet('very_good', ['very_good']);
                    setRunSubmitResults(r);
                    setSubmissionId(r.submissionId || null);
                    props.onSubmitResults && props.onSubmitResults(r);
                } else {
                    !wrongCount && trySet('norm', ['good', 'very_good']);
                    setRunResults(r);
                    props.onChangeRunResults && props.onChangeRunResults(r);
                    r.firstError && setActiveCaseInd(r.firstError.ind);
                }
            })
            .catch(e => {
                console.error(e);
                setRunLoading(false);
                // 503 от api: «проверка временно недоступна» — это не fail решения.
                if (isCheckError(e)) return showCheckError(params && params.isSubmit, e.checkError);
                const result = {
                    wrongCount: 1, passCount: 0, totalCount: 1,
                    firstError: {ind: 0, res1: {errMsg: e.message ?? e.msg ?? e.error?.message ?? e}, res2: {}},
                    info: {FOLDER: 'local'}
                };
                setRunResults(result);
                props?.onChangeRunResults?.(result);
                setActiveCaseInd(0);
            });
    }

    function showCheckError(isSubmit, checkError) {
        let result = {checkError};
        if (isSubmit) {
            setRunSubmitResults(result);
            setSubmissionId(null);
        } else {
            setRunResults(result);
        }
    }

    function submit() {
        run({isSubmit: true});
    }

    function onChangeCodeLocal(newCode) {
        if (isReadOnlyFile()) return;
        let fileName = getFileName();
        history.files[fileName] = newCode;
        updateCode(newCode);
        typingRef.current.change(newCode, selectedFileInd);
        setHistory(history);
        if (isExam) {
            onChangeCode && onChangeCode(newCode, fileName, getTestCase());
        }
    }

    function onChangeLogsLocal(logs) {
        setLogsReader(logs);
        onChangeLogs && onChangeLogs(logs);
    }

    let caseItem = (cases || [])[activeCaseInd];
    let firstErrorInd = !runResults || runResults.checkError ? -1 : runResults.firstError ? runResults.firstError.ind : 99999;
    let isLogsReader = isLogsRaederFn(jsDetails);

    return {
        // state
        code, logsReader, forceRenderLogs, setForceRenderLogs,
        history, files, selectedFileInd, setSelectedFileInd,
        runLoading, curCasesStr, setCurCasesStr,
        cases, setCases, jsDetails,
        topTab, setTopTab,
        runResults, runSubmitResults, submissionId,
        activeCaseInd, setActiveCaseInd,
        data,
        // computed
        question, caseItem, firstErrorInd, isLogsReader,
        // actions
        run, submit, buildTestCases,
        onChangeCodeLocal, onChangeLogsLocal,
        getFileName, getStarter, getFileExt, updateCode, isReadOnlyFile,
        startTime: startTimeRef.current,
    };
}